import React, { createContext, useContext, useState, useCallback } from 'react';
import { Outlet } from 'react-router-dom';

export interface CartItem {
  lineId: string;
  id: number;
  name: string;
  price?: number;
  quantity: number;
  notes?: string;
}

interface CartContextType {
  items: CartItem[];
  addItem: (product: { id: number; name: string; price?: number }) => void;
  addCustomItem: (product: { id: number; name: string; price?: number }, notes: string, quantity?: number) => void;
  updateQty: (lineId: string, qty: number) => void;
  clearCart: () => void;
  cartCount: number;
  asportoEnabled: boolean;
  setAsportoEnabled: (v: boolean) => void;
  asportoMode: boolean;
  setAsportoMode: (v: boolean) => void;
  cartOpen: boolean;
  setCartOpen: (v: boolean) => void;
  whatsappNumber: string;
  setWhatsappNumber: (v: string) => void;
  businessName: string;
  setBusinessName: (v: string) => void;
  // Sessione tavolo (ordine dal QR del tavolo)
  tableUuid: string | null;
  tableLabel: string;
  setTableSession: (uuid: string, label: string) => void;
  clearTableSession: () => void;
}

export const CartContext = createContext<CartContextType | null>(null);

export const CartProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [asportoEnabled, setAsportoEnabled] = useState(false);
  const [asportoMode, setAsportoMode] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [tableUuid, setTableUuid] = useState<string | null>(
    typeof window !== 'undefined' ? sessionStorage.getItem('table_uuid') : null
  );
  const [tableLabel, setTableLabel] = useState<string>(
    typeof window !== 'undefined' ? (sessionStorage.getItem('table_label') || '') : ''
  );

  const setTableSession = useCallback((uuid: string, label: string) => {
    sessionStorage.setItem('table_uuid', uuid);
    sessionStorage.setItem('table_label', label);
    setTableUuid(uuid);
    setTableLabel(label);
  }, []);

  const clearTableSession = useCallback(() => {
    sessionStorage.removeItem('table_uuid');
    sessionStorage.removeItem('table_label');
    setTableUuid(null);
    setTableLabel('');
  }, []);

  const newLineId = (productId: number) =>
    `${productId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

  const addItem = useCallback((product: { id: number; name: string; price?: number }) => {
    setItems(prev => {
      // Incrementa solo una riga "semplice" (senza personalizzazioni)
      const existing = prev.find(i => i.id === product.id && !i.notes);
      if (existing) return prev.map(i => i.lineId === existing.lineId ? { ...i, quantity: i.quantity + 1 } : i);
      return [...prev, { lineId: newLineId(product.id), id: product.id, name: product.name, price: product.price, quantity: 1 }];
    });
  }, []);

  const addCustomItem = useCallback((product: { id: number; name: string; price?: number }, notes: string, quantity: number = 1) => {
    setItems(prev => [...prev, {
      lineId: newLineId(product.id), id: product.id, name: product.name,
      price: product.price, quantity, notes: notes || undefined,
    }]);
  }, []);

  const updateQty = useCallback((lineId: string, qty: number) => {
    if (qty <= 0) setItems(prev => prev.filter(i => i.lineId !== lineId));
    else setItems(prev => prev.map(i => i.lineId === lineId ? { ...i, quantity: qty } : i));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const cartCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items, addItem, addCustomItem, updateQty, clearCart, cartCount,
      asportoEnabled, setAsportoEnabled,
      asportoMode, setAsportoMode,
      cartOpen, setCartOpen,
      whatsappNumber, setWhatsappNumber,
      businessName, setBusinessName,
      tableUuid, tableLabel, setTableSession, clearTableSession,
    }}>
      {children ?? <Outlet />}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
